import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import isEqual from "lodash/isEqual";
import axiosInstance, { BASE_URL } from "../../../utils/config/config";
import { getPractioner } from "../../../redux/actions/user.dispatcher";
import { fetchMemberDirectoryStatus } from "../../../utils/FetchGhlUser/fetchGhlUser";
import {
  GHL_DIRECTORY_STATUS_FIELD_ID,
  ERROR_MESSAGES,
  MULTI_VALUE_FIELDS,
} from "./Resources.constants";
import { getInitialFormData, sanitizeFormDataForSave } from "./Resources.utils";

export const useResources = () => {
  const dispatch = useDispatch();
  const { practitioner } = useSelector((state) => state.user);

  const [directoryStatus, setDirectoryStatus] = useState(false);
  const [modalState, setModalState] = useState(false);
  const [disclaimerModalState, setDisclaimerModalState] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [formData, setFormData] = useState(getInitialFormData(practitioner));
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorState, setErrorState] = useState({
    error: false,
    message: "",
  });

  useEffect(() => {
    dispatch(getPractioner());
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      if (!practitioner?.iaihId) return;

      const response = await fetchMemberDirectoryStatus(practitioner.iaihId);
      const status =
        response?.contact?.customField?.find(
          (field) => field.id === GHL_DIRECTORY_STATUS_FIELD_ID
        )?.value === "approved";

      setDirectoryStatus(status);

      if (status && practitioner?.memberDirectoryStatus !== true) {
        await axiosInstance.post("user/practitioner/create", {
          iaihId: practitioner.iaihId,
          memberDirectoryStatus: true,
        });
        dispatch(getPractioner());
      }
    };
    fetchData();
  }, [practitioner, dispatch]);

  useEffect(() => {
    if (practitioner) {
      const data = getInitialFormData(practitioner);
      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(practitioner)));
    }
  }, [practitioner]);

  const dataURItoBlob = async (file, fieldName) => {
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      setLoading(true);
      const response = await axios.post(`${BASE_URL}aws/upload`, uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploadedUrl = response.data.fileUrl;
      setFormData((prev) => ({ ...prev, [fieldName]: uploadedUrl }));
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setErrorState({ error: false, message: "" });

    if (
      formData.phone &&
      (isNaN(formData.phone) ||
        formData.phone.length < 10 ||
        formData.phone.length > 13)
    ) {
      setErrorState({
        error: true,
        message: ERROR_MESSAGES.INVALID_PHONE,
      });
      return;
    }

    const sanitizedFormData = sanitizeFormDataForSave(
      formData,
      MULTI_VALUE_FIELDS
    );

    if (isEqual(sanitizedFormData, originalData)) {
      setErrorState({
        error: true,
        message: ERROR_MESSAGES.NO_CHANGES,
      });
      return;
    }

    try {
      setModalState(false);
      setLoading(true);

      const response = await axiosInstance.post(
        "user/practitioner/create",
        sanitizedFormData
      );

      if (response.status === 200) {
        setLoading(false);
        setSuccess(true);
        setErrorState({ error: false, message: "" });
        dispatch(getPractioner());
        setOriginalData(JSON.parse(JSON.stringify(sanitizedFormData)));
      }
    } catch (error) {
      console.error(error);
      setErrorState({
        error: true,
        message: ERROR_MESSAGES.SAVE_FAILED,
      });
    } finally {
      setLoading(false);
      setModalState(false);
    }
  };

  const handleDisclaimerSave = () => {
    setDisclaimerModalState(false);
    setModalState(true);
  };

  return {
    directoryStatus,
    modalState,
    setModalState,
    disclaimerModalState,
    setDisclaimerModalState,
    formData,
    setFormData,
    loading,
    success,
    setSuccess,
    errorState,
    setErrorState,
    dataURItoBlob,
    handleSave,
    handleDisclaimerSave,
    practitioner,
  };
};
